import React from 'react';
import { Box, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';
import ClientReportDetails from './ClientReportDetails';
import { reportNumber, reportRate } from './clientPurchaseReportUtils';
import { reportPanelSx, reportSectionTitleSx, reportTableSx } from './clientReportStyles';

export function purchaseProgressionCsvRows(response) {
  const progression = response.purchase_progression;
  if (!progression) return [];
  const frequency = response.order_frequency_30d || {};
  return [
    [], ['Repeat purchases', 'Days after first purchase', 'Eligible first purchasers', 'Converted', 'Unconverted', 'Pending', 'Without first purchase', 'Excluded', 'Rate %', 'Mean days among converted', 'Median days among converted'],
    ...['second_purchase', 'third_purchase'].flatMap((key) => Object.entries(progression[key]).map(([days, metric]) => [
      key, days, metric.eligible, metric.converted, metric.unconverted, metric.pending_maturity, metric.without_first_purchase, metric.excluded, metric.rate, metric.mean_days, metric.median_days,
    ])),
    [], ['Observed conversion time', 'Sample size', 'Mean days', 'Median days'],
    ['Basis', response.conversion_times?.basis],
    ...['registration_to_first', 'first_to_second'].map((key) => {
      const metric = response.conversion_times?.[key] || {};
      return [key, metric.sample_size, metric.mean_days, metric.median_days];
    }),
    [], ['Frequency during first 30 days after registration', 'Value'],
    ['Eligible registered clients', frequency.eligible], ['Pending', frequency.pending_maturity],
    ['Excluded', frequency.excluded], ['Purchases', frequency.orders],
    ['Orders per registered client', frequency.orders_per_client],
    ['Orders per purchasing client', frequency.orders_per_purchaser],
  ];
}

function RepeatTable({ title, metrics }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 750, mb: 0.75 }}>{title}</Typography>
      {mobile ? (
        <Stack component="ul" aria-label={title} spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {Object.entries(metrics).map(([days, metric]) => (
            <Box component="li" key={days} sx={{ p: 1.25, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Within {days} days</Typography>
                <Typography sx={{ color: '#1d4ed8', fontSize: '1.15rem', fontWeight: 800 }}>{reportRate(metric.rate)}</Typography>
              </Stack>
              <Typography sx={{ color: '#475569', fontSize: '0.8rem' }}>
                {reportNumber(metric.converted)} of {reportNumber(metric.eligible)} eligible clients
                {metric.pending_maturity > 0 && ` · ${reportNumber(metric.pending_maturity)} pending`}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : <TableContainer tabIndex={0} role="region" aria-label={`${title} details`} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Table size="small" aria-label={title} sx={{ minWidth: 400, ...reportTableSx }}>
          <TableHead><TableRow>
            {['Days after first', 'Converted / eligible', 'Rate'].map((label, index) => <TableCell key={label} align={index ? 'right' : 'left'}>{label}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>{Object.entries(metrics).map(([days, metric]) => (
            <TableRow key={days}>
              <TableCell>{days} days</TableCell>
              <TableCell align="right">
                {reportNumber(metric.converted)} / {reportNumber(metric.eligible)}
                {metric.pending_maturity > 0 && <Typography variant="caption" component="div" sx={{ color: '#92400e' }}>{reportNumber(metric.pending_maturity)} pending</Typography>}
              </TableCell>
              <TableCell align="right">{reportRate(metric.rate)}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </TableContainer>}
    </Box>
  );
}

export default function ClientsPurchaseProgression({ response, status }) {
  if (!response?.purchase_progression) return null;
  const times = response.conversion_times;
  const frequency = response.order_frequency_30d;
  return (
    <Stack component="section" aria-label="Repeat purchase conversion" spacing={1.5} sx={reportPanelSx}>
      <Typography variant="h6" sx={reportSectionTitleSx}>Repeat purchases</Typography>
      {status === 'loading' && <LinearProgress aria-label="Refreshing repeat purchases" />}
      <Typography variant="body2" sx={{ color: '#475569' }}>
        Windows start at the first purchase. Rates use first purchasers with the full follow-up window.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        <RepeatTable title="Second purchase after first purchase" metrics={response.purchase_progression.second_purchase} />
        <RepeatTable title="Third purchase after first purchase" metrics={response.purchase_progression.third_purchase} />
      </Box>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 750 }}>Purchase timing & frequency</Typography>
      <Stack component="dl" spacing={1} sx={{ m: 0 }}>
        {[
          { title: 'Registration → first purchase', value: `${reportNumber(times.registration_to_first.mean_days, 2)} days`, detail: `Observed mean · ${reportNumber(times.registration_to_first.sample_size)} clients` },
          { title: 'First → second purchase', value: `${reportNumber(times.first_to_second.mean_days, 2)} days`, detail: `Observed mean · ${reportNumber(times.first_to_second.sample_size)} clients` },
          { title: 'Orders per client in first 30 days', value: reportNumber(frequency.orders_per_client, 3), detail: `${reportNumber(frequency.eligible)} eligible registrations, including non-buyers${frequency.pending_maturity > 0 ? ` · ${reportNumber(frequency.pending_maturity)} pending` : ''}` },
        ].map(({ title, value, detail }) => (
          <Box key={title} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 0.5, py: 0.75, borderBottom: '1px solid #e2e8f0' }}>
            <Box component="dt" sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#0f172a', fontWeight: 700 }}>{title}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>{detail}</Typography>
            </Box>
            <Typography component="dd" sx={{ m: 0, fontSize: '1rem', fontWeight: 800, color: '#1d4ed8', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
          </Box>
        ))}
      </Stack>
      <ClientReportDetails title="Timing details & definitions">
        <Stack spacing={1.5}>
          <Typography variant="body2">Observed means describe all converters in the selected registration cohort; they do not compare client groups after the same number of days since registration. Median registration → first purchase: {reportNumber(times.registration_to_first.median_days, 2)} days. Median first → second purchase: {reportNumber(times.first_to_second.median_days, 2)} days.</Typography>
          <Typography variant="body2">Window timings below describe eligible clients who converted inside each window. Clients without a first purchase are not eligible for repeat conversion. Pending clients need more observation time; — means no eligible clients or observed conversion time.</Typography>
          {[
            ['Second purchase', response.purchase_progression.second_purchase],
            ['Third purchase', response.purchase_progression.third_purchase],
          ].map(([title, metrics]) => (
            <Box key={title}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{title} after first purchase</Typography>
              {Object.entries(metrics).map(([days, metric]) => (
                <Typography key={days} variant="body2" sx={{ mt: 0.5 }}>Within {days} days: mean {reportNumber(metric.mean_days, 2)} days · median {reportNumber(metric.median_days, 2)} days</Typography>
              ))}
            </Box>
          ))}
          <Typography variant="body2">First 30 days after registration: {reportNumber(frequency.orders)} purchases across {reportNumber(frequency.eligible)} eligible clients. Per purchasing client: {reportNumber(frequency.orders_per_purchaser, 3)} orders.</Typography>
        </Stack>
      </ClientReportDetails>
    </Stack>
  );
}
